/**
 * On-canvas layer for the Real-Time GEX workspace: MMEC level labels and per-strike profile
 * bars, drawn inside the main pane so they follow pan / zoom / price-scale drags exactly.
 *
 * The level LINES themselves (and their price-axis tags) are ordinary price lines; this
 * primitive adds the in-chart label the TradingView overlay shows —
 *   "MMEC·SPX·0D·GF · CW 7745.92"
 * — and the two profiles anchored to the price axis (gamma by strike, RTL net flow by strike).
 */
import type {
    IPrimitivePaneRenderer,
    IPrimitivePaneView,
    ISeriesPrimitive,
    SeriesAttachedParameter,
    Time,
} from "lightweight-charts";

export interface LevelLine {
    price: number;
    abbr: string[];
    color: string;
    solid: boolean;
    width: number;
}

export interface StrikeBar {
    strike: number;
    gamma: number;
    flow: number;
}

export interface LiveGexLayerOptions {
    symbol: string;
    dteShort: string;
    align: "L" | "M" | "R";
    showProfiles: boolean;
    background: string;
}

const GAMMA_FILL = "rgba(96,165,250,0.78)";
/* Design System preview (?ds=1): net GEX profile in --series-gex, labels in JetBrains Mono. */
const DS_GAMMA_FILL = "rgba(121,134,203,0.8)";
const dsOn = (): boolean => {
    try {
        return document.documentElement.getAttribute("data-mmec-ds") === "1";
    } catch {
        return false;
    }
};
const FLOW_UP = "rgba(38,166,154,0.92)";
const FLOW_DOWN = "rgba(239,83,80,0.92)";
const PROFILE_MAX_W = 150;
const ALIGN_X: Record<LiveGexLayerOptions["align"], number> = { L: 0.3, M: 0.585, R: 0.8 };

class LayerRenderer implements IPrimitivePaneRenderer {
    constructor(private p: LiveGexPrimitive) {}

    draw(target: {
        useMediaCoordinateSpace: (
            fn: (scope: { context: CanvasRenderingContext2D; mediaSize: { width: number; height: number } }) => void,
        ) => void;
    }): void {
        const param = this.p.params;
        if (!param) return;
        const { lines, strikes, options: o } = this.p;
        target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
            const { series } = param;
            const w = mediaSize.width;
            const h = mediaSize.height;

            const ds = dsOn();
            if (o.showProfiles && strikes.length) {
                let mg = 1;
                let mf = 1;
                for (const s of strikes) {
                    mg = Math.max(mg, Math.abs(s.gamma));
                    mf = Math.max(mf, Math.abs(s.flow));
                }
                // bar thickness follows the strike spacing on screen, capped
                let step = 9;
                if (strikes.length > 1) {
                    const a = series.priceToCoordinate(strikes[0].strike);
                    const b = series.priceToCoordinate(strikes[1].strike);
                    if (a != null && b != null) step = Math.max(2, Math.min(11, Math.abs(a - b) - 2));
                }
                for (const s of strikes) {
                    const y = series.priceToCoordinate(s.strike);
                    if (y == null || y < 4 || y > h - 4) continue;
                    const gw = (Math.abs(s.gamma) / mg) * PROFILE_MAX_W;
                    ctx.fillStyle = ds ? DS_GAMMA_FILL : GAMMA_FILL;
                    ctx.fillRect(w - 178 - gw, y - step / 2, gw, step);
                    const fw = (Math.abs(s.flow) / mf) * PROFILE_MAX_W;
                    ctx.fillStyle = s.flow >= 0 ? FLOW_UP : FLOW_DOWN;
                    ctx.fillRect(w - 10 - fw, y - step / 2, fw, step);
                }
            }

            ctx.font = ds ? "600 11px 'JetBrains Mono', 'SF Mono', Consolas, monospace" : '700 12px Inter, "Trebuchet MS", sans-serif';
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            const x = w * ALIGN_X[o.align];
            for (const l of lines) {
                const y = series.priceToCoordinate(l.price);
                if (y == null || y < 8 || y > h - 8) continue;
                const text = `MMEC·${o.symbol}·${o.dteShort}·${l.abbr.join(" · ")} ${l.price.toFixed(2)}`;
                const tw = ctx.measureText(text).width;
                ctx.fillStyle = o.background; // knock the line out behind the label
                ctx.fillRect(x - tw / 2 - 8, y - 9, tw + 16, 18);
                ctx.fillStyle = l.color;
                ctx.fillText(text, x, y + 0.5);
            }
        });
    }
}

class LayerView implements IPrimitivePaneView {
    private r: LayerRenderer;
    constructor(p: LiveGexPrimitive) {
        this.r = new LayerRenderer(p);
    }
    zOrder(): "top" {
        return "top";
    }
    renderer(): IPrimitivePaneRenderer {
        return this.r;
    }
}

export class LiveGexPrimitive implements ISeriesPrimitive<Time> {
    params: SeriesAttachedParameter<Time, "Candlestick"> | null = null;
    lines: LevelLine[] = [];
    strikes: StrikeBar[] = [];
    options: LiveGexLayerOptions = {
        symbol: "SPX",
        dteShort: "0D",
        align: "M",
        showProfiles: true,
        background: "#0b0d14",
    };
    private view: LayerView;

    constructor() {
        this.view = new LayerView(this);
    }

    attached(param: SeriesAttachedParameter<Time, "Candlestick">): void {
        this.params = param;
    }

    detached(): void {
        this.params = null;
    }

    paneViews(): IPrimitivePaneView[] {
        return [this.view];
    }

    updateAllViews(): void {
        /* state is read on each draw */
    }

    setData(lines: LevelLine[], strikes: StrikeBar[]): void {
        this.lines = lines;
        this.strikes = [...strikes].sort((a, b) => a.strike - b.strike);
        this.params?.requestUpdate();
    }

    setOptions(patch: Partial<LiveGexLayerOptions>): void {
        this.options = { ...this.options, ...patch };
        this.params?.requestUpdate();
    }
}
